import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableHeader } from "@/components/InmateDetails/styles";
import BookingQueueStepFilter from "./BookingQueueStepFilter";
import BookingQueueTable, { BOOKING_QUEUE_HEADERS } from "./BookingQueueTable";
import Button from "@/common/elements/Button";
import { PlusOutlined } from "@ant-design/icons";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { Popconfirm } from "antd";
import BookingQueueDetailsModal from "./BookingQueueDetailsModal";
import { setBookings } from "@/slices/bookingsSlice";
import {
  hasCheckSecurity,
  isRemoveFromQueueStep,
  getBookingQueueStepConstantName,
  getBookingQueueStepShortName,
  getIncompleteBookingQueuePrerequisite,
  getBookingQueueStepLabelByShortName,
  getMissingBookingQueuePrerequisiteMessageLines,
  getRefListNameByID,
} from "@/util/common_function";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { validateBookingQueueStep } from "@/util/bookingQueueStepValidators";
import useDialog from "@/hooks/useDialog";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const BookingQueueDetails = ({ startingPoint = "", forceCompleteStep }) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const { bookings, bookingsFieldsEnabled, manualBookingData } = useSelector(
    (state) => state.bookings
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). The grid itself is rendered
  // by the child BookingQueueTable, so headers/draggable/ref are passed
  // through as props rather than owned there.
  const {
    headers: bookingQueueHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:BookingQueueDetails", BOOKING_QUEUE_HEADERS);
  const [bookingQueueDetails, setBookingQueueDetails] = useState([]);

  const [queueDetailsModal, setQueueDetailsModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const autoOpenedStepRef = useRef(null);

  // Update bookingQueueDetails whenever bookings changes
  useEffect(() => {
    setBookingQueueDetails(
      bookings?.bookingsQueue?.[0]?.bookingQueueDetails || []
    );
  }, [bookings]);

  useEffect(() => {
    if (manualBookingData?.isManualBooking) {
      handleOpenAddModal();
    }
  }, []);

  // ForceComplete steps (Booking Complete / Remove-from-Queue) arriving from
  // the Booking Queue screen: auto-open this tab's details modal pre-selected
  // to that step, same as clicking its checkbox would. This only stages it —
  // the user still has to review/Save in the modal, matching legacy's
  // ForceComplete behavior of landing the user ready-to-confirm, not silently
  // marking it complete. Guarded so it only fires once per step per landing,
  // even as bookingQueueDetails updates while the booking finishes loading.
  useEffect(() => {
    if (!forceCompleteStep?.refListItemGlobalID) return;
    if (autoOpenedStepRef.current === forceCompleteStep.refListItemGlobalID) {
      return;
    }

    const alreadyComplete = bookingQueueDetails?.some(
      (d) =>
        d.queStepID === forceCompleteStep.refListItemGlobalID &&
        d.complete === true
    );

    if (alreadyComplete) {
      autoOpenedStepRef.current = forceCompleteStep.refListItemGlobalID;
      return;
    }

    if (bookingQueueDetails?.length) {
      autoOpenedStepRef.current = forceCompleteStep.refListItemGlobalID;
      setModalData({
        value: forceCompleteStep.refListItemGlobalID,
        label: forceCompleteStep.defaultString,
      });
      setIsEditing(false);
      setQueueDetailsModal(true);
    }
  }, [forceCompleteStep, bookingQueueDetails]);
  const handleStepChange = (step, checked) => {
    if (!checked) {
      // Remove any details with queStepID equal to the step value.
      const updatedDetails = bookingQueueDetails.filter(
        (detail) => detail.queStepID !== step
      );
      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [{ bookingQueueDetails: updatedDetails }],
        })
      );
    }
  };

  const handleOpenAddModal = () => {
    setModalData(null);
    setIsEditing(false);
    setQueueDetailsModal(true);
  };

  const handleEditRow = (row) => {
    setQueueDetailsModal(true);
    setIsEditing(true);
  };

  const handleRowClick = (row) => {
    setModalData(row);
  };

  const handleSave = (data) => {
    const sanitizedData = {
      bookingQueueDetailID: data?.bookingQueueDetailID,
      queStepID: data?.queStepID,
      completionDate: data?.completionDate,
      completionTime: data?.completionTime,
      creatorStaffID: userDetails?.staffId,
      completionStaffID: data?.completionStaffID,
      comments: data?.comments,
      complete: data?.complete || false,
      // Legacy's FinalStep: only the Remove-from-Queue (CP) step, when
      // completed, tells the backend to drop this booking off the active
      // queue. Derived from the step's constantName, not trusted from the
      // form (which has no finalStep field and always saved false here).
      finalStep: isRemoveFromQueueStep(data?.queStepID),
    };

    // Remove bookingQueueDetailID if it contains "new"
    if (
      typeof sanitizedData.bookingQueueDetailID === "string" &&
      sanitizedData.bookingQueueDetailID.includes("new")
    ) {
      delete sanitizedData.bookingQueueDetailID;
    }

    // Mirrors legacy's ValidateBookingQueueOverride/ValidateBookingQueueStep,
    // which blocks a step's completion at save time if its configured
    // Prerequisites aren't complete yet — enforced here since this screen
    // (the Bookings module's own Booking Queue Details tab) has no other
    // gate, unlike the Booking Queue page's step icons.
    if (sanitizedData.complete) {
      const shortName = getBookingQueueStepShortName(sanitizedData.queStepID);
      const missingPrereqShort = getIncompleteBookingQueuePrerequisite(
        shortName,
        bookingQueueDetails
      );
      if (missingPrereqShort) {
        const currentStepLabel =
          getRefListNameByID("BookingQueueStep", sanitizedData.queStepID) ||
          shortName;
        const missingPrereqLabel = getBookingQueueStepLabelByShortName(
          missingPrereqShort
        );
        const [line1, line2] = getMissingBookingQueuePrerequisiteMessageLines(
          currentStepLabel,
          missingPrereqLabel
        );
        showDialog("warning", {
          title: "IMATS",
          content: (
            <>
              {line1}
              <br />
              <br />
              {line2}
            </>
          ),
          confirmButtonText: "OK",
        });
        return false;
      }

      // Mirrors legacy's per-step "Validator" attribute (Eval'd routine run
      // when a step is completed, e.g. ValidateBQCharges).
      const validatorError = validateBookingQueueStep(
        getBookingQueueStepConstantName(sanitizedData.queStepID),
        bookings
      );
      if (validatorError) {
        showDialog("warning", {
          title: "IMATS",
          content: validatorError,
          confirmButtonText: "OK",
        });
        return false;
      }
    }

    if (isEditing) {
      // Replace the row entirely with the sanitized data
      const updatedDetails = bookingQueueDetails?.map((item) =>
        (item.bookingQueueDetailID &&
          item.bookingQueueDetailID === data.bookingQueueDetailID) ||
        item.queStepID === data.queStepID
          ? sanitizedData
          : item
      );

      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [{ bookingQueueDetails: updatedDetails }],
        })
      );
    } else {
      // Add new row with sanitized data
      const newRow = { ...sanitizedData, id: bookingQueueDetails.length + 1 };
      dispatch(
        setBookings({
          ...bookings,
          bookingsQueue: [
            { bookingQueueDetails: [...bookingQueueDetails, newRow] },
          ],
        })
      );
    }
  };

  const handleCloseModal = () => {
    setQueueDetailsModal(false);
    setModalData(null);
    setIsEditing(false);
  };

  return (
    <>
      <BookingQueueStepFilter
        onStepChange={handleStepChange}
        onOpenModal={(step) => {
          setModalData(step);
          setQueueDetailsModal(true);
        }}
        bookingQueueDetails={bookingQueueDetails}
        disabled={!bookingsFieldsEnabled}
      />

      <TableHeader style={{ marginTop: "15px", flexWrap: "wrap" }}>
        <h6>Booking Queue Details</h6>
        <div>
          <StyledGridRow style={{ width: "100%" }}>
            <StyledGridCol>
              <Button
                style={{ marginRight: "10px" }}
                onClick={handleOpenAddModal}
                disabled={
                  !bookingsFieldsEnabled ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  )
                }
              >
                Add
              </Button>
            </StyledGridCol>
            {bookingQueueDetails.length > 0 && (
              <StyledGridCol>
                <Button
                  style={{ marginRight: "10px" }}
                  onClick={handleEditRow}
                  disabled={
                    !modalData ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.module,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    )
                  }
                >
                  Details
                </Button>
              </StyledGridCol>
            )}
            <StyledGridCol>
              {/* Config — drag-reorder/resize columns; only while Modify is active */}
              {!isConfiguring ? (
                <Button
                  htmlType="button"
                  onClick={startConfiguring}
                >
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button htmlType="button">
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              )}
            </StyledGridCol>
          </StyledGridRow>
        </div>
      </TableHeader>
      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.module,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS
      ) && (
        <BookingQueueTable
          ref={dataTableRef}
          headers={bookingQueueHeaders}
          draggable={isConfiguring}
          data={bookingQueueDetails}
          selected={!modalData}
          onRowClick={handleRowClick}
          onDoubleRowClick={handleEditRow}
        />
      )}

      {queueDetailsModal && (
        <BookingQueueDetailsModal
          show={queueDetailsModal}
          onHide={handleCloseModal}
          stepData={modalData}
          onSave={handleSave}
          bookings={bookings}
          isEditing={isEditing}
          startingPoint={startingPoint}
        />
      )}
    </>
  );
};

export default BookingQueueDetails;
